import React, { useState } from 'react';
import { ArrowRight, Tag, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Categories = () => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopyCoupon = () => {
    navigator.clipboard.writeText(t('newsletter.code')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Map for localized product routes
  const productRoutes: Record<string, string> = {
    pt: '/produtos',
    en: '/products',
    es: '/products',
    de: '/produkte'
  };

  const getProductsRoute = () => productRoutes[i18n.language] || '/products';

  const categories = [
    {
      id: 1,
      slug: 'detox',
      name: t('categories.detoxTitle'),
      description: t('categories.detoxDesc'),
      image: "https://images.pexels.com/photos/1346347/pexels-photo-1346347.jpeg?auto=compress&cs=tinysrgb&w=600",
      productCount: 12,
      gradient: "from-green-400 to-blue-500"
    },
    {
      id: 2,
      slug: 'emagrecimento',
      name: t('categories.weightLossTitle'),
      description: t('categories.weightLossDesc'),
      image: "https://images.pexels.com/photos/3768916/pexels-photo-3768916.jpeg?auto=compress&cs=tinysrgb&w=600",
      productCount: 8,
      gradient: "from-pink-400 to-red-500"
    },
    {
      id: 3,
      slug: 'fibra',
      name: t('categories.fiberTitle'),
      description: t('categories.fiberDesc'),
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=600",
      productCount: 15,
      gradient: "from-purple-400 to-pink-500"
    },
    {
      id: 4,
      slug: 'packs',
      name: t('categories.packsTitle'),
      description: t('categories.packsDesc'),
      image: "https://images.pexels.com/photos/3735170/pexels-photo-3735170.jpeg?auto=compress&cs=tinysrgb&w=600",
      productCount: 6,
      gradient: "from-yellow-400 to-orange-500"
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('categories.title')}
            <span className="bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] bg-clip-text text-transparent">
              {' '}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('categories.subtitle')}
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`${getProductsRoute()}?category=${category.slug}`}
              className="group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                {/* Background Image */}
                <div className="relative h-80">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />

                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t ${category.gradient} opacity-75 group-hover:opacity-85 transition-opacity duration-300`}></div>

                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
                    <div className="transform group-hover:translate-y-0 translate-y-2 transition-transform duration-300">
                      <h3 className="text-xl font-bold mb-2">{category.name}</h3>
                      <p className="text-sm opacity-90 mb-3">{category.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {category.productCount} {t('categories.detoxProducts')}
                        </span>
                        <div className="flex items-center text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">
                          {t('categories.viewMore')}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Special Offers Section */}
        <div className="mt-16 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] rounded-3xl p-8 lg:p-12 text-white text-center">
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            {t('newsletter.title')}
          </h3>
          <p className="text-lg mb-6 opacity-90">
            {t('newsletter.joinSubtitle')}
          </p>

          {/* Coupon Box */}
          <div className="flex flex-col items-center mb-6">
            <p className="text-sm font-medium uppercase tracking-widest opacity-80 mb-2">
              {t('newsletter.couponLabel')}
            </p>
            <button
              onClick={handleCopyCoupon}
              className="group flex items-center gap-3 bg-white text-[#4ECDC4] border-4 border-dashed border-white border-opacity-60 px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
              title={t('newsletter.copyCoupon')}
            >
              <Tag className="w-6 h-6 shrink-0" />
              <span className="font-black text-2xl tracking-widest font-mono">{t('newsletter.code')}</span>
              {copied
                ? <Check className="w-5 h-5 text-green-500 shrink-0" />
                : <Copy className="w-5 h-5 opacity-60 group-hover:opacity-100 shrink-0" />
              }
            </button>
            <p className="text-sm mt-3 opacity-90 font-medium">
              {t('newsletter.couponHint')}
            </p>
          </div>

          <div className="flex justify-center">
            <Link
              to={getProductsRoute()}
              className="bg-white text-[#4ECDC4] px-10 py-3 rounded-full font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 inline-block"
            >
              {t('newsletter.button')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Categories;
